"use client";

import { CircleAlert, Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog } from "@nitro/ui";

const LANGUAGES = [
  {
    code: "en",
    label: "English",
    note: "Automatic translation is enabled. Some simracing terms may be translated incorrectly.",
  },
  {
    code: "fr",
    label: "Français",
    note: "La traduction automatique est activée. Certains termes de simracing peuvent être traduits de façon incorrecte.",
  },
  {
    code: "de",
    label: "Deutsch",
    note: "Die automatische Übersetzung ist aktiviert. Einige Simracing-Begriffe können falsch übersetzt werden.",
  },
  {
    code: "it",
    label: "Italiano",
    note: "La traduzione automatica è attiva. Alcuni termini del simracing potrebbero essere tradotti in modo errato.",
  },
  {
    code: "pt",
    label: "Português",
    note: "A tradução automática está ativada. Alguns termos de simracing podem ser traduzidos incorretamente.",
  },
  {
    code: "ru",
    label: "Русский",
    note: "Включен автоматический перевод. Некоторые термины симрейсинга могут быть переведены неточно.",
  },
];

const STORAGE_KEY = "nitro-language";
const SOURCE_LANGUAGE = "es";
const LANGUAGE_EVENT = "nitro-language-change";

export function LanguageTranslator() {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(SOURCE_LANGUAGE);

  useEffect(() => {
    const savedLanguage = window.localStorage.getItem(STORAGE_KEY) || SOURCE_LANGUAGE;
    setLanguage(savedLanguage);
    emitLanguageChange(savedLanguage);
    loadGoogleTranslate();
    suppressGoogleChrome();

    const observer = new MutationObserver(suppressGoogleChrome);
    observer.observe(document.body, { childList: true, subtree: true });

    if (savedLanguage !== SOURCE_LANGUAGE) {
      window.setTimeout(() => applyGoogleLanguage(savedLanguage), 500);
    }

    return () => observer.disconnect();
  }, []);

  function selectLanguage(nextLanguage) {
    setLanguage(nextLanguage);
    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    emitLanguageChange(nextLanguage);
    applyGoogleLanguage(nextLanguage);
    setOpen(false);
  }

  const activeLanguage = LANGUAGES.find((item) => item.code === language);

  return (
    <>
      <button aria-label={`Cambiar idioma: ${activeLanguage?.label || "Español"}`} aria-haspopup="dialog" aria-expanded={open} className="nitro-language-button notranslate" onClick={() => setOpen(true)} title={`Idioma: ${activeLanguage?.label || "Español"}`} translate="no" type="button">
        <Languages size={16} aria-hidden="true" />
        <span>{activeLanguage?.label || "Idioma"}</span>
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} title="Idioma" description="Selecciona el idioma para traducir el portal automáticamente." closeLabel="Cerrar selector de idioma" size="sm" className="notranslate" translate="no">
            <div className="nitro-language-list">
              <button className={language === SOURCE_LANGUAGE ? "is-active" : ""} onClick={() => selectLanguage(SOURCE_LANGUAGE)} type="button">
                <Languages size={16} />
                Español
              </button>
              {LANGUAGES.map((item) => (
                <button className={language === item.code ? "is-active" : ""} key={item.code} onClick={() => selectLanguage(item.code)} type="button">
                  <Languages size={16} />
                  {item.label}
                </button>
              ))}
            </div>
      </Dialog>

      <div className="nitro-google-translate notranslate" id="google_translate_element" translate="no" />
    </>
  );
}

export function TranslationNotice() {
  const [language, setLanguage] = useState(SOURCE_LANGUAGE);

  useEffect(() => {
    setLanguage(window.localStorage.getItem(STORAGE_KEY) || SOURCE_LANGUAGE);

    function onLanguageChange(event) {
      setLanguage(event.detail?.language || SOURCE_LANGUAGE);
    }

    window.addEventListener(LANGUAGE_EVENT, onLanguageChange);
    return () => window.removeEventListener(LANGUAGE_EVENT, onLanguageChange);
  }, []);

  const activeLanguage = LANGUAGES.find((item) => item.code === language);
  if (!activeLanguage) return null;

  return (
    <div className="nitro-translation-note notranslate" translate="no">
      <CircleAlert size={15} />
      <span>{activeLanguage.note}</span>
    </div>
  );
}

function loadGoogleTranslate() {
  if (window.google?.translate?.TranslateElement) return;

  window.googleTranslateElementInit = () => {
    if (!window.google?.translate?.TranslateElement) return;

    new window.google.translate.TranslateElement(
      {
        autoDisplay: false,
        includedLanguages: LANGUAGES.map((item) => item.code).join(","),
        pageLanguage: SOURCE_LANGUAGE,
      },
      "google_translate_element"
    );
  };

  if (document.querySelector('script[data-nitro-google-translate="true"]')) return;

  const script = document.createElement("script");
  script.async = true;
  script.dataset.nitroGoogleTranslate = "true";
  script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  document.body.appendChild(script);
}

function applyGoogleLanguage(language) {
  if (language === SOURCE_LANGUAGE) {
    clearGoogleTranslateCookie();
    window.location.reload();
    return;
  }

  setGoogleTranslateCookie(language);

  const select = document.querySelector(".goog-te-combo");
  if (!select) {
    window.setTimeout(() => applyGoogleLanguage(language), 350);
    return;
  }

  select.value = language;
  select.dispatchEvent(new Event("change"));
  window.setTimeout(suppressGoogleChrome, 150);
  window.setTimeout(suppressGoogleChrome, 700);
}

function emitLanguageChange(language) {
  window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT, { detail: { language } }));
}

function suppressGoogleChrome() {
  document.body.style.top = "0";
  document.documentElement.style.marginTop = "0";

  [
    ".goog-te-banner-frame",
    ".goog-te-banner-frame.skiptranslate",
    "iframe.goog-te-banner-frame",
    "body > .skiptranslate",
    ".VIpgJd-ZVi9od-ORHb-OEVmcd",
    ".goog-te-balloon-frame",
    "#goog-gt-tt",
  ].forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => {
      element.style.display = "none";
      element.style.visibility = "hidden";
      element.style.height = "0";
    });
  });
}

function setGoogleTranslateCookie(language) {
  const value = `/es/${language}`;
  writeCookie("googtrans", value, 365);
}

function clearGoogleTranslateCookie() {
  writeCookie("googtrans", "", -1);
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const cookieValue = `${name}=${value}; expires=${expires}; path=/`;
  document.cookie = cookieValue;

  const host = window.location.hostname;
  if (host.includes(".")) {
    const parts = host.split(".");
    const rootDomain = `.${parts.slice(-2).join(".")}`;
    document.cookie = `${cookieValue}; domain=${rootDomain}`;
  }
}
